import { hashSeed } from '../../effects/atmosphere/atmospherePlacement';
import type { PlatformAccentBounds } from '../organicPlatformAccent';
import { drawHarborMaterial, type HarborMaterialStyle } from '../materials/harborMaterials';
import { drawTownQuay, drawTownTimber, validTownSurface,
  type TownMaterialGraphics, type TownQuayPalette, type TownTimberPalette } from '../materials/townSurfaceMaterials';

export interface TownMaterialProfile {
  readonly quay: TownQuayPalette;
  readonly timber: TownTimberPalette;
  /** Local floor offsets: scenery replaces these pixels, never the floor's collision. */
  readonly harbor?: Readonly<{
    x: number;
    width: number;
    style: Omit<HarborMaterialStyle, 'seed'>;
  }>;
}

/** Scene-independent composition: one existing Graphics, at most three floor spans. */
export function drawProfiledTownMaterial(
  graphics: TownMaterialGraphics,
  platform: PlatformAccentBounds,
  isLedge: boolean,
  groundY: number,
  profile: TownMaterialProfile,
): void {
  if (!validTownSurface(platform)) return;
  const seed = hashSeed(`${platform.id ?? ''}:${platform.x}:${platform.y}:${platform.width}`);
  if (isLedge) {
    // The authored facade supplies posts below the cap, behind all real terrain.
    drawTownTimber(graphics, platform, groundY, seed, profile.timber, 'none');
    return;
  }
  const harbor = profile.harbor;
  if (!harbor || platform.y !== groundY || !Number.isFinite(harbor.x) || !Number.isFinite(harbor.width)
    || harbor.x < 0 || harbor.width <= 0 || harbor.x + harbor.width > platform.width) {
    drawTownQuay(graphics, platform, seed, profile.quay);
    return;
  }
  if (harbor.x > 0) drawTownQuay(graphics, { ...platform, width: harbor.x }, seed, profile.quay);
  drawHarborMaterial(graphics, { ...platform, x: platform.x + harbor.x, width: harbor.width },
    { ...harbor.style, seed });
  const right = harbor.x + harbor.width;
  if (right < platform.width) drawTownQuay(graphics,
    { ...platform, x: platform.x + right, width: platform.width - right }, seed + 1, profile.quay);
}
